import { Button, Flex, message, TablePaginationConfig } from "antd";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import { getColumns } from "./utils/table";
import { Supplier } from "@/types/supplier";
import { DEFAULT_PAGINATION } from "@/constants/table";
import { useMemo, useState } from "react";
import useModal from "@/hooks/useModal";
import {
  useGetExpenseSuppliers,
  useGetExpenseSuppliersOptions,
} from "../../hooks/useExpenseSuppliers";
import {
  useDeleteExpenseSupplier,
  useRestoreExpenseSupplier,
  useUpdateExpenseSupplier,
} from "../../hooks/useExpenseSuppliers";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";

const ExpenseSuppliers = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<Supplier>();

  const { mutate: deleteExpenseSupplier } = useDeleteExpenseSupplier();
  const { mutate: restoreExpenseSupplier } = useRestoreExpenseSupplier();
  const { mutate: updateExpenseSupplier } = useUpdateExpenseSupplier();

  const {
    data: expenseSuppliers,
    isFetching,
    refetch,
  } = useGetExpenseSuppliers({
    params: {
      include_inactive: true,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseSuppliersOptions({
    params: {
      only: "name,contact_person,email,phone",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      name: (options || []).reduce((acc, item) => {
        if (item.name && !acc.some((option) => option.value === item?.name)) {
          acc.push({
            text: item.name,
            value: item.name,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      contact_person: (options || []).reduce((acc, item) => {
        if (
          item.contact_person &&
          !acc.some((option) => option.value === item?.contact_person)
        ) {
          acc.push({
            text: item.contact_person,
            value: item.contact_person,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      email: (options || []).reduce((acc, item) => {
        if (item.email && !acc.some((option) => option.value === item?.email)) {
          acc.push({
            text: item.email,
            value: item.email,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      phone: (options || []).reduce((acc, item) => {
        if (item.phone && !acc.some((option) => option.value === item?.phone)) {
          acc.push({
            text: item.phone,
            value: item.phone,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        restoreExpenseSupplier(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully activated expense supplier");
            },
          }
        );
      } else {
        deleteExpenseSupplier(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully deactivated expense supplier");
            },
          }
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const handleToggleReview = async (id: string, inReview: boolean) => {
    try {
      const currentRecord = expenseSuppliers?.data?.find(
        (supplier) => supplier.id === id
      );
      if (!currentRecord) {
        message.error("Supplier not found");
        return;
      }

      updateExpenseSupplier(
        {
          id,
          name: currentRecord.name,
          is_reviewed: inReview,
        },
        {
          onSuccess: () => {
            message.success(
              inReview
                ? "Successfully marked expense supplier for review"
                : "Successfully removed expense supplier from review"
            );
          },
        }
      );
    } catch (error) {
      throw error;
    }
  };

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end" align="center">
        <Button type="primary" onClick={openAddModal}>
          New Supplier
        </Button>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onToggleActive: handleToggleActive,
            onToggleReview: handleToggleReview,
            filterOptions,
          })
        }
        dataSource={expenseSuppliers?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            showSizeChanger: true,
          },
          dataPagination: expenseSuppliers?.pagination,
          onChange: setPagination,
        }}
        scroll={{
          x: "max-content",
        }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <CreateModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSuccess={() => refetch()}
      />
      <EditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSuccess={() => refetch()}
      />
    </Flex>
  );
};

export default ExpenseSuppliers;
